/**
 * The written side of a call: what a message is, and the rules about one.
 *
 * The chat is deliberately thinner than the whiteboard. LiveKit already
 * carries chat over the room's own channel, so there is no wire format to
 * invent here — only the shape the room's UI works in, and the handful of
 * decisions that are ours rather than the transport's.
 */

import type { MeetingFileEntry } from "./meetingFile";

export type MeetingChatMessage = {
    /** LiveKit's id for the message, stable across the edit of one. */
    id: string;
    /** Milliseconds since the epoch, as the sender stamped it. */
    timestamp: number;
    body: string;
    /** The sending identity, or "" when the room could not say. */
    authorId: string;
    /** The sender's display name, empty when they joined without one. */
    authorName: string;
    /** Whether this side sent it. Decides which way the bubble faces. */
    mine: boolean;
};

/**
 * How much one message may carry.
 *
 * LiveKit refuses data packets above roughly 15 KiB and a chat message is
 * sent as one. Two thousand characters is far below that and still more than
 * anyone types into a call, so the limit is felt as a counter rather than as
 * a rejection.
 */
export const MAX_CHAT_MESSAGE_LENGTH = 2000;

/** Which side of the call someone is on, read from the minted identity. */
export type ChatAuthorRole = "therapist" | "guest";

/**
 * Who sent this, when there is no name to show.
 *
 * The identity is `therapist-<uuid>` or `guest-<uuid>` — minted by the
 * meeting service, never chosen by the person — so it is the one thing a
 * message always carries. A patient who joined without typing a name would
 * otherwise be labelled `guest-2fde51e9…` above every line they wrote.
 */
export function chatAuthorRole(identity: string): ChatAuthorRole {
    return identity.startsWith("therapist-") ? "therapist" : "guest";
}

/**
 * What actually gets sent, or "" when there is nothing worth sending.
 *
 * Trimmed because a message is posted with Enter and a stray newline is not
 * a message; capped because the transport has a ceiling and silently losing
 * the end of a long paragraph would be worse than cutting it where the
 * counter says it will be cut.
 */
export function normalizeChatBody(raw: string): string {
    return raw.trim().slice(0, MAX_CHAT_MESSAGE_LENGTH);
}

/**
 * How many of these have not been seen.
 *
 * Only the other person's messages count. Your own arrive back through the
 * same list, and a badge that lit up because you wrote something would teach
 * people to ignore it.
 */
export function unreadCount(entries: { mine: boolean }[], readCount: number): number {
    return entries.slice(Math.max(0, readCount)).filter((entry) => !entry.mine).length;
}

/** One line of the chat panel: something written, or a file handed across. */
export type MeetingChatEntry =
    | ({ kind: "message" } & MeetingChatMessage)
    | ({ kind: "file" } & MeetingFileEntry);

/**
 * Messages and files as one conversation, in the order they were sent.
 *
 * They arrive through different channels — LiveKit's chat and a byte stream —
 * but to the person reading they are the same exchange, and a file shown
 * apart from the sentence that introduced it ("this is the sheet I mentioned")
 * loses its context. The sort is stable, so two entries stamped in the same
 * millisecond keep the order they arrived in.
 */
export function chatTimeline(
    messages: MeetingChatMessage[],
    files: MeetingFileEntry[]
): MeetingChatEntry[] {
    const entries: MeetingChatEntry[] = [
        ...messages.map((message) => ({ kind: "message" as const, ...message })),
        ...files.map((file) => ({ kind: "file" as const, ...file })),
    ];
    return entries.sort((a, b) => a.timestamp - b.timestamp);
}
